import tinycolor from 'tinycolor2';
import {Theme} from '../types/Theme';
import {ThemeStyle} from '../context/ThemeProvider';
import {SurfaceState} from '../types/SurfaceState';
import ColorHelpers from './ColorHelpers';
import {SurfaceDensity} from '../types/SurfaceDensity';

export default class ThemeHelpers {

  public static createOutlinedInputThemeStyle(
    borderColor: string,
    state: SurfaceState
  ): ThemeStyle {

    let backgroundColor = '#FFFFFF';
    if (state === 'disabled') {
      backgroundColor = '#F5F5F5';
    }

    return {
      borderColor: borderColor,
      backgroundColor: backgroundColor,
      textColor: 'rgba(0,0,0,0.65)',
      borderWidth: '1px',
      rounding: '4px',
      boundarySpacing: '4px',
      effectColor: 'rgba(24,144,255,0.2)',
      highlightColor: '#1890ff'
    };
  }

  public static createFlatButtonThemeStyle(
    backgroundColor: string,
    textColor: string,
    density: SurfaceDensity,
    state: SurfaceState
  ): ThemeStyle {

    let borderColor: string;
    if (state === 'disabled') {
      backgroundColor = '#F5F5F5';
      textColor = 'rgba(0,0,0,0.65)';
      borderColor = 'rgba(0,0,0,0.1)';
    } else if (state === 'hovered') {
      borderColor = backgroundColor;
    } else if (state === 'pressed') {
      backgroundColor = ColorHelpers.getInteractionCueColor(backgroundColor, 'light');
      borderColor = backgroundColor;
    } else if (state === 'selected') {
      backgroundColor = 'transparent';
      textColor = 'rgba(0,0,0,0.65)';
      borderColor = '#1890ff';
    } else {
      backgroundColor = 'transparent';
      textColor = 'rgba(0,0,0,0.65)';
      borderColor = 'rgba(0,0,0,0.1)';
    }

    const spacing =  density === 'compact' ? '4px' : '8px';

    return {
      backgroundColor: backgroundColor,
      borderColor: borderColor,
      borderWidth: '1px',
      effectColor: borderColor,
      fontSize: '14px',
      textColor: textColor,
      rounding: '16px',
      boundarySpacing: spacing,
      childSpacing: spacing
    };
  }

  public static createElevatedButtonThemeStyle(
    backgroundColor: string,
    textColor: string,
    density: SurfaceDensity,
    state: SurfaceState
  ): ThemeStyle {

    if (state === 'disabled') {
      backgroundColor = '#F5F5F5';
      textColor = 'rgba(0, 0, 0, 0.25)';
    } else if (state === 'pressed' || state === 'selected' || state === 'hovered') {
      backgroundColor = ColorHelpers.getInteractionCueColor(backgroundColor, 'light');
    }

    const spacing =  density === 'compact' ? '4px' : '8px';

    return {
      backgroundColor: backgroundColor,
      highlightColor: '#1890ff',
      effectColor: backgroundColor,
      fontSize: '14px',
      textColor: textColor,
      rounding: '16px',
      boundarySpacing: spacing,
      childSpacing: spacing
    };
  }

  public static createFlatPanelThemeStyle(
    backgroundColor: string,
    density: SurfaceDensity
  ): ThemeStyle {

    const spacing =  density === 'compact' ? '5px' : '10px';

    return {
      backgroundColor: backgroundColor,
      rounding: '3px',
      fontSize: '14px',
      textColor: 'rgba(0, 0, 0, 0.65)',
      boundarySpacing: spacing,
      childSpacing: spacing
    };
  }

  public static createFloatingPanelThemeStyle(
    backgroundColor: string,
    density: SurfaceDensity
  ): ThemeStyle {

    const spacing =  density === 'compact' ? '5px' : '10px';

    return {
      backgroundColor: backgroundColor,
      borderColor: ColorHelpers.getOutlineColor(backgroundColor, 'light'),
      borderWidth: '1px',
      rounding: '3px',
      fontSize: '14px',
      textColor: 'rgba(0, 0, 0, 0.65)',
      boundarySpacing: spacing,
      childSpacing: spacing,
      elevation: '4px'
    };
  }

  public static createElevatedPanelThemeStyle(
    backgroundColor: string,
    density: SurfaceDensity
  ): ThemeStyle {

    const spacing =  density === 'compact' ? '5px' : '10px';

    return {
      backgroundColor: backgroundColor,
      borderColor: ColorHelpers.getOutlineColor(backgroundColor, 'light'),
      borderWidth: '1px',
      rounding: '3px',
      fontSize: '14px',
      textColor: 'rgba(0, 0, 0, 0.65)',
      boundarySpacing: spacing,
      childSpacing: spacing
    };
  }

  public static getOutlineColor(color: string, theme: Exclude<Theme, 'auto' | 'unthemed'>): string {
    switch (theme) {
      case 'light':
        return tinycolor(color).darken(25).toRgbString();
      case 'dark':
        return tinycolor(color).lighten(25).toRgbString();
      default:
        return '#FF00FF';
    }
  }

  public static getInteractionCueColor(color: string, theme: Exclude<Theme, 'auto' | 'unthemed'>): string {
    switch (theme) {
      case 'light':
        return tinycolor(color).lighten(10).toRgbString();
      case 'dark':
        return tinycolor(color).darken(10).toRgbString();
      default:
        return '#FF00FF';
    }
  }

  public static lighten(color: string, percent: number): string {
    return tinycolor(color).lighten(10).toRgbString();
  }

  public static pickBestContrastColor(backgroundColor: string, options: string[]) {
    return tinycolor.mostReadable(backgroundColor, options).toRgbString();
  }
}
